// client/src/components/Achievements/achievementsConfig.js

// --- QUEST THEMES (UI colors per quest id) ---
export const QUEST_THEME = {
  knowledge_hunter: "indigo",
  perfect_score: "orange",
  archivist: "purple",
  early_bird: "green",
};

export const RANK_STEPS = [
  {
    label: "Novice",
    sub: "Lvl 1-5",
    min: 1,
    max: 5,
    icon: "fa-seedling",
    lineClass: "bg-blue-500",
    xpBarClass:
      "bg-gradient-to-r from-blue-300 to-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.45)]",
    indicatorClass: "bg-blue-50 text-blue-700 border-blue-200",
    stepClasses: {
      completedCircle: "bg-blue-500 text-white ring-4 ring-white shadow-sm",
      currentCircle:
        "bg-white border-2 border-blue-500 text-blue-600 ring-4 ring-blue-100 shadow-md transform -translate-y-1 w-10 h-10 text-lg",
      currentText: "text-blue-600",
      currentSubText: "text-blue-500 font-bold",
    },
  },
  {
    label: "Apprentice",
    sub: "Lvl 6-15",
    min: 6,
    max: 15,
    icon: "fa-book-reader",
    lineClass: "bg-sky-500",
    xpBarClass:
      "bg-gradient-to-r from-sky-300 to-sky-500 shadow-[0_0_10px_rgba(14,165,233,0.45)]",
    indicatorClass: "bg-sky-50 text-sky-700 border-sky-200",
    stepClasses: {
      completedCircle: "bg-sky-500 text-white ring-4 ring-white shadow-sm",
      currentCircle:
        "bg-white border-2 border-sky-500 text-sky-600 ring-4 ring-sky-100 shadow-md transform -translate-y-1 w-10 h-10 text-lg",
      currentText: "text-sky-600",
      currentSubText: "text-sky-500 font-bold",
    },
  },
  {
    label: "Scholar",
    sub: "Lvl 16-30",
    min: 16,
    max: 30,
    icon: "fa-user-graduate",
    lineClass: "bg-violet-500",
    xpBarClass:
      "bg-gradient-to-r from-violet-300 to-violet-500 shadow-[0_0_10px_rgba(139,92,246,0.45)]",
    indicatorClass: "bg-violet-50 text-violet-700 border-violet-200",
    stepClasses: {
      completedCircle: "bg-violet-500 text-white ring-4 ring-white shadow-sm",
      currentCircle:
        "bg-white border-2 border-violet-500 text-violet-600 ring-4 ring-violet-100 shadow-md transform -translate-y-1 w-10 h-10 text-lg",
      currentText: "text-violet-600",
      currentSubText: "text-violet-500 font-bold",
    },
  },
  {
    label: "Sage",
    sub: "Lvl 31-49",
    min: 31,
    max: 49,
    icon: "fa-brain",
    lineClass: "bg-amber-500",
    xpBarClass:
      "bg-gradient-to-r from-amber-300 to-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.45)]",
    indicatorClass: "bg-amber-50 text-amber-700 border-amber-200",
    stepClasses: {
      completedCircle: "bg-amber-500 text-white ring-4 ring-white shadow-sm",
      currentCircle:
        "bg-white border-2 border-amber-500 text-amber-600 ring-4 ring-amber-100 shadow-md transform -translate-y-1 w-10 h-10 text-lg",
      currentText: "text-amber-600",
      currentSubText: "text-amber-500 font-bold",
    },
  },
  {
    label: "Grandmaster",
    sub: "Lvl 50",
    min: 50,
    max: 50,
    icon: "fa-crown",
    lineClass: "bg-rose-500",
    xpBarClass:
      "bg-gradient-to-r from-rose-300 to-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.45)]",
    indicatorClass: "bg-rose-50 text-rose-700 border-rose-200",
    stepClasses: {
      completedCircle: "bg-rose-500 text-white ring-4 ring-white shadow-sm",
      currentCircle:
        "bg-white border-2 border-rose-500 text-rose-600 ring-4 ring-rose-100 shadow-md transform -translate-y-1 w-10 h-10 text-lg",
      currentText: "text-rose-600",
      currentSubText: "text-rose-500 font-bold",
    },
  },
];

export const MANILA_OFFSET_MS = 8 * 60 * 60 * 1000;

export const CUSTOM_STYLES = `
  @keyframes shine { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
  @keyframes bounceIn { 0% { transform: scale(0.8); opacity: 0; } 60% { transform: scale(1.1); } 100% { transform: scale(1); opacity: 1; } }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  .progress-bar-shine { background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent); animation: shine 2s infinite; }
  .animate-bounce-in { animation: bounceIn 0.5s ease-out forwards; opacity: 0; }
  .badge-locked { filter: grayscale(100%) opacity(0.4); transition: all 0.3s; }
  .badge-locked:hover { filter: grayscale(100%) opacity(0.6); }
`;

// supports { xp, level, ... } OR { success:true, data:{ xp, level, ... } }
export const normalizeDashPayload = (payload) => {
  if (!payload) return null;
  if (payload.success && payload.data) return payload.data;
  return payload;
};

export const normalizeSearchUsersPayload = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (payload.success && Array.isArray(payload.data)) return payload.data;
  if (payload.success && Array.isArray(payload.data?.users)) return payload.data.users;
  if (payload.success && Array.isArray(payload.data?.results)) return payload.data.results;
  if (Array.isArray(payload.users)) return payload.users;
  if (Array.isArray(payload.results)) return payload.results;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data?.users)) return payload.data.users;
  if (Array.isArray(payload.data?.results)) return payload.data.results;
  return [];
};

export const normalizeIncomingRequestsPayload = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (payload.success && Array.isArray(payload.data)) return payload.data;
  if (payload.success && Array.isArray(payload.data?.requests)) return payload.data.requests;
  if (Array.isArray(payload.requests)) return payload.requests;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data?.requests)) return payload.data.requests;
  return [];
};

export function getIdentity(u) {
  return {
    id: String(u?._id || u?.id || u?.userId || ""),
    email: String(u?.email || "").toLowerCase(),
  };
}

export function getObjectId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return String(value._id || value.id || value.userId || "");
  }
  return String(value);
}

export function sameIdentity(a, b) {
  const A = getIdentity(a);
  const B = getIdentity(b);

  if (A.id && B.id && A.id === B.id) return true;
  if (A.email && B.email && A.email === B.email) return true;
  return false;
}

export function isCurrentUser(u, myIdentity) {
  const target = getIdentity(u);

  if (myIdentity.id && target.id && myIdentity.id === target.id) return true;
  if (myIdentity.email && target.email && myIdentity.email === target.email) return true;
  return false;
}

// Next Manila midnight countdown helper
export function getMsUntilNextManilaMidnight() {
  const now = new Date();
  const manilaLikeUTC = new Date(now.getTime() + MANILA_OFFSET_MS);

  const y = manilaLikeUTC.getUTCFullYear();
  const m = manilaLikeUTC.getUTCMonth();
  const d = manilaLikeUTC.getUTCDate();

  const nextMidnightManilaLikeUTC = new Date(Date.UTC(y, m, d + 1, 0, 0, 0, 0));
  const nextMidnightUTC = new Date(nextMidnightManilaLikeUTC.getTime() - MANILA_OFFSET_MS);

  return Math.max(0, nextMidnightUTC.getTime() - now.getTime());
}

export function formatHMS(ms) {
  const total = Math.floor(ms / 1000);
  const h = String(Math.floor(total / 3600)).padStart(2, "0");
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export function formatShortDate(dateValue) {
  if (!dateValue) return "";
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
